// Considered: DialogWrapper, CustomDialogAtom — rejected because the design contract forbids a
// competing drawer or modal (design/metaspace-tools/README.md § interaction contract): the
// inspector must REPLACE the panel's content in place, with a back control, not float above it.
// No in-panel detail primitive existed. See spec Reuse Audit (specs/stream-trajectory-tools.md).
//
// Source: design/metaspace-tools/metaspace-interactions.jsx:50-141 (`MSInspector`). IA: back
// header -> title/subtitle -> address row -> sections (rows / derivation / evidence / forensics)
// -> note -> act footer / confirmation. Escape = back.
//
// Sonar S6440: every hook sits above the `if (!item) return null` early return.

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { InspectorAct, InspectorPayload } from 'types/inspector';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import KeyValueRowAtom from '../../atoms/KeyValueRow/KeyValueRowAtom';
import AddressRowMolecule from '../../molecules/AddressRow/AddressRowMolecule';
import DerivationStepsMolecule from '../../molecules/DerivationSteps/DerivationStepsMolecule';
import './ToolInspectorOrganism.scss';

export interface ToolInspectorOrganismProps {
  /** The payload to render. `null` renders nothing — the panel shows its normal content instead. */
  item: InspectorPayload | null;
  /** The name of the tool the inspector was opened from (e.g. "Stream", "Trajectory") — the back label. */
  backLabel: string;
  onClose: () => void;
  /** Appends an observation. Reject to report a failure — the footer keeps the acts live.
   * `inputValue` carries the typed value for acts declaring `input` (Correct…, Reply). */
  onAct: (act: InspectorAct, item: InspectorPayload, inputValue?: string) => Promise<void> | void;
  onCopyLink?: (address: string) => void;
  className?: string;
}

const ToolInspectorOrganism: React.FC<ToolInspectorOrganismProps> = ({
  item,
  backLabel,
  onClose,
  onAct,
  onCopyLink,
  className,
}) => {
  const { t } = useTranslation();
  const [activeActId, setActiveActId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingInputAct, setPendingInputAct] = useState<InspectorAct | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  // Reset act state whenever the inspector opens a different item — a stale confirmation/error
  // from the previous item must never bleed into the next.
  useEffect(() => {
    setActiveActId(null);
    setConfirmation(null);
    setError(null);
    setPendingInputAct(null);
    setInputValue('');
  }, [item?.address]);

  // Focus the act's input as it appears. Managed rather than `autoFocus`, which fires only on
  // MOUNT: picking a second input-bearing act while the first one's field is open swaps the
  // placeholder and leaves focus where it was, so the field the person is now answering is not
  // the one their keystrokes reach. Keyed on the act, so each one claims focus in turn.
  //
  // Moving focus here is a response to the person's own click on that act, not an on-load grab
  // (S9379): the field did not exist until they asked for it.
  useEffect(() => {
    if (pendingInputAct) inputRef.current?.focus();
  }, [pendingInputAct]);

  useEffect(() => {
    if (!item) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [item, onClose]);

  if (!item) return null;

  const handleAct = async (act: InspectorAct, typedValue?: string) => {
    setActiveActId(act.id);
    setError(null);
    try {
      await onAct(act, item, typedValue);
      setConfirmation(act.confirmationLabel ?? act.label);
      setPendingInputAct(null);
      setInputValue('');
    } catch {
      setError(t('toolInspector.actFooter.error'));
    } finally {
      setActiveActId(null);
    }
  };

  const handleActClick = (act: InspectorAct) => {
    if (act.input) {
      setPendingInputAct((prev) => (prev?.id === act.id ? null : act));
      setInputValue('');
      return;
    }
    void handleAct(act);
  };

  const canSubmitInput = inputValue.trim().length > 0;
  const submitInput = () => {
    if (!pendingInputAct || !canSubmitInput) return;
    void handleAct(pendingInputAct, inputValue.trim());
  };

  return (
    <div className={['tool-inspector-organism', className].filter(Boolean).join(' ')}>
      <div className="tool-inspector-organism__header">
        <button
          type="button"
          className="tool-inspector-organism__back focus-ring"
          onClick={onClose}
        >
          <GlyphAtom glyph="lucide:chevron-left" />
          {backLabel}
        </button>
        <span className="tool-inspector-organism__divider" />
        <GlyphAtom glyph={item.icon} className="tool-inspector-organism__kind-icon" />
        <span className="tool-inspector-organism__kind-label">
          {t(`toolInspector.kind.${item.kind}`, { defaultValue: item.kind })}
        </span>
      </div>

      <div className="tool-inspector-organism__body">
        <div className="tool-inspector-organism__title-block">
          <div className="tool-inspector-organism__title">{item.title}</div>
          {item.subtitle && <div className="tool-inspector-organism__subtitle">{item.subtitle}</div>}
        </div>

        {item.address && (
          <div className="tool-inspector-organism__section tool-inspector-organism__section--address">
            <AddressRowMolecule address={item.address} onCopy={onCopyLink} />
          </div>
        )}

        {item.rows && item.rows.length > 0 && (
          <div className="tool-inspector-organism__section">
            <div className="tool-inspector-organism__section-label">
              {item.rowsLabel || t('toolInspector.section.fact')}
            </div>
            {item.rows.map((row) => (
              <KeyValueRowAtom key={row.label} label={row.label} value={row.value} mono={row.mono} />
            ))}
          </div>
        )}

        {item.derivation && item.derivation.length > 0 && (
          <div className="tool-inspector-organism__section">
            <div className="tool-inspector-organism__section-label">
              {t('toolInspector.section.derivation')}
            </div>
            <DerivationStepsMolecule steps={item.derivation} />
          </div>
        )}

        {item.evidence && item.evidence.length > 0 && (
          <div className="tool-inspector-organism__section">
            <div className="tool-inspector-organism__section-label">
              {t('toolInspector.section.evidence')}
            </div>
            {item.evidence.map((e) => (
              <a
                key={e.label}
                href={e.href}
                target="_blank"
                rel="noreferrer"
                className="tool-inspector-organism__evidence-row"
              >
                <GlyphAtom glyph="lucide:paperclip" />
                {e.label}
              </a>
            ))}
          </div>
        )}

        {item.forensics && item.forensics.length > 0 && (
          <div className="tool-inspector-organism__section">
            <div className="tool-inspector-organism__section-label">
              {t('toolInspector.section.forensics')}
            </div>
            {item.forensics.map((row) => (
              <KeyValueRowAtom key={row.label} label={row.label} value={row.value} mono />
            ))}
          </div>
        )}

        {item.note && (
          <div className="tool-inspector-organism__note">
            <MessageAtom severity="warn" text={item.note} />
          </div>
        )}
      </div>

      {item.acts.length > 0 && (
        <div className="tool-inspector-organism__footer">
          {error && (
            <div className="tool-inspector-organism__footer-error">
              <MessageAtom severity="error" text={error} />
            </div>
          )}
          {confirmation ? (
            <div className="tool-inspector-organism__confirmation">
              <GlyphAtom glyph="lucide:check-circle" />
              {confirmation}
            </div>
          ) : (
            <>
              <div className="tool-inspector-organism__footer-label">
                {t('toolInspector.actFooter.label')}
              </div>
              <div className="tool-inspector-organism__acts">
                {item.acts.map((act) => (
                  <ButtonAtom
                    key={act.id}
                    shape="pill"
                    size="small"
                    variant={act.variant}
                    icon={act.icon}
                    label={act.label}
                    disabled={act.disabled}
                    loading={activeActId === act.id}
                    onClick={() => handleActClick(act)}
                  />
                ))}
              </div>
              {pendingInputAct && (
                <div className="tool-inspector-organism__input-row">
                  {pendingInputAct.input?.multiline ? (
                    <textarea
                      className="tool-inspector-organism__input tool-inspector-organism__input--multiline focus-ring"
                      placeholder={pendingInputAct.input.placeholder}
                      value={inputValue}
                      rows={3}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitInput();
                      }}
                      ref={(el) => { inputRef.current = el; }}
                    />
                  ) : (
                    <input
                      className="tool-inspector-organism__input focus-ring"
                      type={pendingInputAct.input?.kind === 'number' ? 'number' : 'text'}
                      placeholder={pendingInputAct.input?.placeholder}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitInput();
                      }}
                      ref={(el) => { inputRef.current = el; }}
                    />
                  )}
                  <ButtonAtom
                    shape="pill"
                    size="small"
                    variant="primary"
                    icon="lucide:check"
                    label={t('toolInspector.input.submit', { defaultValue: 'Submit' })}
                    disabled={!canSubmitInput}
                    loading={activeActId === pendingInputAct.id}
                    onClick={submitInput}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

ToolInspectorOrganism.displayName = 'ToolInspectorOrganism';

export default ToolInspectorOrganism;
